"use client";

import Link from "next/link";
import { useSearchParams } from "next/navigation";
import { useQuery } from "convex/react";
import { api } from "../../../convex/_generated/api";
import { Navbar } from "@/components/layout/Navbar";
import { Footer } from "@/components/layout/Footer";
import { Button } from "@/components/ui/button";
import { usePageTitle } from "@/lib/usePageTitle";
import { type SubscriptionStatus, type SubscriptionTier } from "@/lib/billing/plans";

function tierLabel(tier: SubscriptionTier) {
  if (tier === "free") return "Free";
  return tier.charAt(0).toUpperCase() + tier.slice(1);
}

function statusLabel(status: SubscriptionStatus) {
  if (status === "none") return "No active subscription";
  return status.replace(/_/g, " ");
}

export default function CheckoutCancelPage() {
  usePageTitle("Checkout Canceled");

  const searchParams = useSearchParams();
  const requestedPlan = searchParams.get("plan");
  const user = useQuery(api.users.getCurrentUser);

  const currentTier = (user?.subscriptionTier ?? "free") as SubscriptionTier;
  const currentStatus = (user?.subscriptionStatus ?? "none") as SubscriptionStatus;

  return (
    <div className="flex min-h-screen flex-col bg-background">
      <Navbar />

      <main className="flex flex-1 flex-col items-center justify-center px-4 py-16 text-center">
        <div className="mx-auto max-w-xl space-y-6">
          <div className="flex items-center justify-center">
            <div className="flex h-16 w-16 items-center justify-center rounded-full bg-yellow-500/10">
              <svg
                className="h-8 w-8 text-yellow-600"
                fill="none"
                viewBox="0 0 24 24"
                stroke="currentColor"
                strokeWidth={2}
              >
                <path strokeLinecap="round" strokeLinejoin="round" d="M12 9v2m0 4h.01M12 3a9 9 0 100 18 9 9 0 000-18z" />
              </svg>
            </div>
          </div>

          <div className="space-y-2">
            <h1 className="text-3xl font-bold text-foreground">Checkout Canceled</h1>
            <p className="text-muted">
              You left Stripe checkout before confirming payment. No new subscription was activated.
            </p>
          </div>

          {requestedPlan && (
            <p className="text-sm text-muted">
              Selected plan:{" "}
              <span className="font-medium capitalize text-foreground">{requestedPlan}</span>
            </p>
          )}

          <div className="rounded-md border border-border bg-background/50 px-4 py-3 text-sm text-muted">
            Current plan:{" "}
            <span className="font-medium text-foreground">{tierLabel(currentTier)}</span>
            {" • "}
            Status:{" "}
            <span className="font-medium capitalize text-foreground">{statusLabel(currentStatus)}</span>
          </div>

          <div className="flex flex-col gap-3 sm:flex-row sm:justify-center">
            <Link href="/pricing">
              <Button>Try Checkout Again</Button>
            </Link>
            <Link href="/dashboard/settings/billing">
              <Button variant="outline">Back to Billing</Button>
            </Link>
          </div>
        </div>
      </main>

      <Footer />
    </div>
  );
}
